/**
 * SoleadLab 2.0 — State Panel (Floating Overlay, C2.5)
 *
 * Full-detail panel for site state snapshots: current summary, history
 * timeline, asset-level state, and recompute controls. Consumes C2.3
 * (recompute) and C2.4 (timeline + latest) endpoints via stateStore — no
 * frontend state computation. All status/confidence values are the verbatim
 * backend outputs.
 *
 * Tabs:
 *   Summary  — latest site snapshot (portfolio/performance/risk + summaries)
 *   History  — paginated site snapshot timeline
 *   Assets   — selectable asset tree with per-node snapshot card
 */

import { useEffect, useState } from 'react';
import { useStateStore } from '@/stores/stateStore';
import { useAssetNodeStore } from '@/stores/assetNodeStore';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import { useTruthStore } from '@/stores/truthStore';
import AssetStateCard from './AssetStateCard';
import type {
  SiteStateSnapshot,
  SiteSummaryJSON,
  PortfolioStatus,
  PerformanceStatus,
  RiskStatus,
  RecomputeResult,
} from '@/types/state';
import type { AssetNode } from '@/services/assetNodeApi';
import type { SiteTruthRow, ConclusionFamily } from '@/types/truth';
import { CONCLUSION_FAMILY_LABELS, ELIGIBILITY_LABELS, ELIGIBILITY_COLORS } from '@/types/truth';
import '@/styles/state.css';

interface StatePanelProps {
  onClose: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

type AnyStatus = PortfolioStatus | PerformanceStatus | RiskStatus;

function StatusBadge({ status }: { status: AnyStatus }) {
  return <span className={`state-badge state-badge--${status}`}>{status}</span>;
}

function ConfidenceBar({ score }: { score: number }) {
  const level = score >= 0.7 ? 'high' : score >= 0.4 ? 'medium' : 'low';
  return (
    <div className="state-confidence">
      <div className="state-confidence__bar">
        <div
          className={`state-confidence__fill state-confidence__fill--${level}`}
          style={{ width: `${Math.round(score * 100)}%` }}
        />
      </div>
      <span>{Math.round(score * 100)}%</span>
    </div>
  );
}

function parseSummary(snapshot: SiteStateSnapshot): SiteSummaryJSON | null {
  try { return JSON.parse(snapshot.summary_json); } catch { return null; }
}

function formatTs(ts: string) {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Truth Summary Mini-Cards ──────────────────────────────────────────────────

const TRUTH_FAMILIES: ConclusionFamily[] = ['performance', 'condition', 'risk'];

function TruthMiniCard({ family, truth }: { family: ConclusionFamily; truth: SiteTruthRow | null }) {
  const label = CONCLUSION_FAMILY_LABELS[family];

  if (!truth) {
    return (
      <div className="truth-mini truth-mini--empty">
        <div className="truth-mini__label">{label}</div>
        <div className="truth-mini__value">Not computed</div>
      </div>
    );
  }

  const eligColor = ELIGIBILITY_COLORS[truth.eligibility_state];
  return (
    <div className="truth-mini">
      <div className="truth-mini__header">
        <span className="truth-mini__label">{label}</span>
        <span className="truth-mini__badge" style={{ background: eligColor }}>
          {ELIGIBILITY_LABELS[truth.eligibility_state]}
        </span>
      </div>
      <div className="truth-mini__bars">
        <div className="truth-mini__bar-row">
          <span>C</span>
          <div className="truth-mini__bar">
            <div className="truth-mini__bar-fill" style={{ width: `${Math.round(truth.completeness_score * 100)}%`, background: '#3b82f6' }} />
          </div>
          <span>{Math.round(truth.completeness_score * 100)}%</span>
        </div>
        <div className="truth-mini__bar-row">
          <span>V</span>
          <div className="truth-mini__bar">
            <div className="truth-mini__bar-fill" style={{ width: `${Math.round(truth.coverage_score * 100)}%`, background: '#8b5cf6' }} />
          </div>
          <span>{Math.round(truth.coverage_score * 100)}%</span>
        </div>
      </div>
      <div className="truth-mini__freshness">{truth.freshness_status}</div>
    </div>
  );
}

function TruthSummarySection() {
  // F-02E-2B: hide the "Compute All" truth-recompute button for visitors.
  const isReadOnly = useIsReadOnlyViewer();
  const truthByFamily = useTruthStore((s) => s.truthByFamily);
  const truthLoading = useTruthStore((s) => s.loading);
  const truthRecompute = useTruthStore((s) => s.recompute);
  const truthRecomputeStatus = useTruthStore((s) => s.recomputeStatus);

  const hasAny = Object.values(truthByFamily).some(t => t !== null);

  const [expanded, setExpanded] = useState(false);

  return (
    <div className="state-panel-truth-section">
      <div
        className="state-panel-truth-section__header"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer' }}
      >
        <span className="state-panel-summary-item__label">
          Site Truth {expanded ? '▾' : '▸'}
        </span>
        {!hasAny && !truthLoading && !isReadOnly && (
          <button
            className="state-btn state-btn--secondary state-btn--sm"
            onClick={(e) => { e.stopPropagation(); truthRecompute('all'); }}
            disabled={truthRecomputeStatus === 'running'}
            type="button"
            style={{ marginLeft: 'auto', fontSize: 10 }}
            data-testid="state-panel-truth-compute-all"
          >
            {truthRecomputeStatus === 'running' ? 'Computing...' : 'Compute All'}
          </button>
        )}
      </div>
      {expanded && (
        <div className="state-panel-truth-section__grid">
          {truthLoading ? (
            <div style={{ fontSize: 11, color: 'var(--sl-color-neutral-400)' }}>Loading...</div>
          ) : (
            TRUTH_FAMILIES.map(f => (
              <TruthMiniCard key={f} family={f} truth={truthByFamily[f]} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Summary Tab ───────────────────────────────────────────────────────────────

function SummaryTab({ snapshot }: { snapshot: SiteStateSnapshot }) {
  const summary = parseSummary(snapshot);

  const conditionCounts = summary?.assets_by_condition ?? {};
  const conditionOrder: string[] = ['healthy', 'watch', 'degraded', 'critical', 'unknown'];

  return (
    <>
      {/* Overview */}
      <div className="state-panel-overview">
        <div className="state-panel-overview__statuses">
          <StatusBadge status={snapshot.portfolio_status} />
          <StatusBadge status={snapshot.performance_status} />
          <StatusBadge status={snapshot.risk_status} />
        </div>
        <div className="state-panel-overview__score-row">
          <ConfidenceBar score={snapshot.confidence_score} />
        </div>
        <div className="state-panel-overview__ts">
          Snapshot: {formatTs(snapshot.snapshot_ts)}
        </div>
      </div>

      {/* Primary signal + dimension summaries */}
      {summary && (
        <div className="state-panel-summary">
          {summary.primary_signal && (
            <div className="state-panel-summary-item">
              <div className="state-panel-summary-item__label">Primary Signal</div>
              {summary.primary_signal}
            </div>
          )}
          {summary.portfolio_summary && (
            <div className="state-panel-summary-item">
              <div className="state-panel-summary-item__label">Portfolio</div>
              {summary.portfolio_summary}
            </div>
          )}
          {summary.performance_summary && (
            <div className="state-panel-summary-item">
              <div className="state-panel-summary-item__label">Performance</div>
              {summary.performance_summary}
            </div>
          )}
          {summary.risk_summary && (
            <div className="state-panel-summary-item">
              <div className="state-panel-summary-item__label">Risk</div>
              {summary.risk_summary}
            </div>
          )}
        </div>
      )}

      {/* Assets by condition */}
      {Object.keys(conditionCounts).length > 0 && (
        <div className="state-panel-condition-grid">
          {conditionOrder.map((c) => {
            const count = (conditionCounts as Record<string, number>)[c] ?? 0;
            return (
              <div key={c} className="state-panel-condition-cell">
                <div className="state-panel-condition-cell__count">{count}</div>
                <div className="state-panel-condition-cell__label">{c}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Finding counts */}
      {summary && (summary.active_finding_count ?? 0) > 0 && (
        <div className="state-panel-summary-item">
          <div className="state-panel-summary-item__label">Findings</div>
          {summary.active_finding_count} active
          {(summary.critical_finding_count ?? 0) > 0 && ` · ${summary.critical_finding_count} critical`}
          {(summary.warning_finding_count ?? 0) > 0 && ` · ${summary.warning_finding_count} warning`}
        </div>
      )}

      {/* Asset coverage */}
      {summary?.asset_coverage && (
        <div className="state-panel-summary-item">
          <div className="state-panel-summary-item__label">Coverage</div>
          {summary.asset_coverage.assets_with_state} / {summary.asset_coverage.total_assets} assets with state
          {summary.asset_coverage.assets_with_data !== summary.asset_coverage.assets_with_state &&
            ` · ${summary.asset_coverage.assets_with_data} with data`}
        </div>
      )}

      {/* Truth Qualification Summary */}
      <TruthSummarySection />
    </>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function HistoryTab() {
  const siteHistory = useStateStore((s) => s.siteHistory);
  const siteHistoryTotal = useStateStore((s) => s.siteHistoryTotal);
  const siteHistoryPage = useStateStore((s) => s.siteHistoryPage);
  const siteHistoryLoading = useStateStore((s) => s.siteHistoryLoading);
  const loadSiteHistory = useStateStore((s) => s.loadSiteHistory);

  const totalPages = Math.max(1, Math.ceil(siteHistoryTotal / PAGE_SIZE));

  useEffect(() => {
    loadSiteHistory(1);
  }, [loadSiteHistory]);

  if (siteHistoryLoading && siteHistory.length === 0) {
    return <div className="state-panel-loading">Loading history…</div>;
  }

  if (!siteHistoryLoading && siteHistory.length === 0) {
    return <div className="state-panel-empty">No snapshot history yet.</div>;
  }

  return (
    <>
      <div className="state-panel-history">
        {siteHistory.map((snap) => {
          const summary = parseSummary(snap);
          return (
            <div key={snap.id} className="state-panel-history-item">
              <span className="state-panel-history-item__ts">{formatTs(snap.snapshot_ts)}</span>
              <div className="state-panel-history-item__statuses">
                <StatusBadge status={snap.portfolio_status} />
                <StatusBadge status={snap.performance_status} />
                <StatusBadge status={snap.risk_status} />
              </div>
              <span className="state-panel-history-item__conf">
                {Math.round(snap.confidence_score * 100)}%
              </span>
              {summary?.primary_signal && (
                <span
                  title={summary.primary_signal}
                  style={{
                    fontSize: 11,
                    color: 'var(--sl-color-neutral-500)',
                    marginLeft: 4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 120,
                  }}
                >
                  {summary.primary_signal}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="state-panel-pagination">
          <button
            className="state-btn state-btn--secondary state-btn--sm"
            disabled={siteHistoryPage <= 1 || siteHistoryLoading}
            onClick={() => loadSiteHistory(siteHistoryPage - 1)}
          >
            ← Prev
          </button>
          <span>Page {siteHistoryPage} / {totalPages} · {siteHistoryTotal} total</span>
          <button
            className="state-btn state-btn--secondary state-btn--sm"
            disabled={siteHistoryPage >= totalPages || siteHistoryLoading}
            onClick={() => loadSiteHistory(siteHistoryPage + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </>
  );
}

// ── Assets Tab ────────────────────────────────────────────────────────────────

const TYPE_ORDER: Record<string, number> = {
  site: 0, station: 1, inverter: 2, mppt: 3, dccb: 4, string: 5, module: 6,
};

function typeSort(a: AssetNode, b: AssetNode): number {
  const ao = TYPE_ORDER[a.assetType] ?? 99;
  const bo = TYPE_ORDER[b.assetType] ?? 99;
  if (ao !== bo) return ao - bo;
  return a.canonicalLabel.localeCompare(b.canonicalLabel);
}

interface AssetsTabProps {
  siteId: string;
}

function AssetsTab({ siteId }: AssetsTabProps) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const items = useAssetNodeStore((s) => s.items);
  const loading = useAssetNodeStore((s) => s.loading);
  const error = useAssetNodeStore((s) => s.error);
  const setSiteId = useAssetNodeStore((s) => s.setSiteId);
  const fetchItems = useAssetNodeStore((s) => s.fetchItems);
  const getChildren = useAssetNodeStore((s) => s.getChildren);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) fetchItems();
  }, [siteId, fetchItems]);

  if (loading) return <div className="state-panel-loading">Loading asset tree…</div>;
  if (error) return <div className="state-panel-empty">{error}</div>;
  if (items.length === 0) {
    return (
      <div className="state-panel-empty">
        No asset nodes found.<br />
        Save base data to generate the asset topology tree.
      </div>
    );
  }

  const itemIds = new Set(items.map((n) => n.id));
  const roots = items
    .filter((n) => !n.parentAssetId || !itemIds.has(n.parentAssetId))
    .sort(typeSort);

  function renderNode(node: AssetNode, depth: number): React.ReactNode {
    const children = getChildren(node.id).sort(typeSort);
    const isSelected = selectedNodeId === node.id;
    return (
      <li key={node.id} style={{ listStyle: 'none', paddingLeft: depth * 12 }}>
        <div
          onClick={() => setSelectedNodeId(isSelected ? null : node.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            borderRadius: 4,
            cursor: 'pointer',
            background: isSelected ? 'var(--sl-color-primary-50, #eff6ff)' : 'transparent',
            border: isSelected ? '1px solid var(--sl-color-primary-200, #bfdbfe)' : '1px solid transparent',
            marginBottom: 2,
          }}
        >
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              padding: '1px 5px',
              borderRadius: 3,
              background: 'var(--sl-color-neutral-200)',
              color: 'var(--sl-color-neutral-700)',
              flexShrink: 0,
            }}
          >
            {node.assetType}
          </span>
          <span style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.displayName ?? node.canonicalLabel}
          </span>
          {node.displayName && (
            <span style={{ fontSize: 10, color: 'var(--sl-color-neutral-400)', flexShrink: 0 }}>
              {node.canonicalLabel}
            </span>
          )}
        </div>

        {/* Asset state card — shown inline when node is selected */}
        {isSelected && (
          <div className="asset-tree-node-state" style={{ paddingLeft: 8, marginBottom: 6 }}>
            <AssetStateCard nodeId={node.id} siteId={siteId} />
          </div>
        )}

        {children.length > 0 && (
          <ul style={{ padding: 0, margin: 0 }}>
            {children.map((child) => renderNode(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--sl-color-neutral-500)', marginBottom: 8 }}>
        {items.length} asset node{items.length !== 1 ? 's' : ''} · click a node to inspect its state
      </div>
      <ul style={{ padding: 0, margin: 0 }}>
        {roots.map((root) => renderNode(root, 0))}
      </ul>
    </div>
  );
}

// ── Recompute summary banner ──────────────────────────────────────────────────

function RecomputeBanner({ result }: { result: RecomputeResult }) {
  const inserted = result.asset_results.filter((r) => r.outcome === 'inserted').length;
  const duplicates = result.asset_results.filter((r) => r.outcome === 'duplicate').length;
  const errors = result.asset_results.filter((r) => r.outcome === 'error').length;
  const siteOutcome = result.site_result?.outcome;

  return (
    <div className="state-panel-recompute-summary">
      Recompute complete · {inserted} asset{inserted !== 1 ? 's' : ''} updated
      {duplicates > 0 && `, ${duplicates} unchanged`}
      {errors > 0 && `, ${errors} error${errors !== 1 ? 's' : ''}`}
      {siteOutcome === 'inserted' && ' · Site snapshot updated'}
      {siteOutcome === 'duplicate' && ' · Site snapshot unchanged'}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

type TabId = 'summary' | 'history' | 'assets';

export default function StatePanel({ onClose }: StatePanelProps) {
  // F-02E-2B: share viewers see read-only state snapshots — Recompute
  // is hidden (POST /sites/:id/state/recompute is not shareReadable).
  const isReadOnly = useIsReadOnlyViewer();
  const siteLatest = useStateStore((s) => s.siteLatest);
  const siteLatestLoading = useStateStore((s) => s.siteLatestLoading);
  const currentSiteId = useStateStore((s) => s.currentSiteId);
  const recomputeStatus = useStateStore((s) => s.recomputeStatus);
  const lastRecompute = useStateStore((s) => s.lastRecompute);
  const triggerRecompute = useStateStore((s) => s.triggerRecompute);

  // Truth store — load when site ID available
  const setTruthSiteId = useTruthStore((s) => s.setSiteId);
  useEffect(() => {
    setTruthSiteId(currentSiteId);
  }, [currentSiteId, setTruthSiteId]);

  const [tab, setTab] = useState<TabId>(siteLatest ? 'summary' : 'history');
  const isRunning = recomputeStatus === 'running';

  // Switch to summary when a new snapshot arrives after recompute
  useEffect(() => {
    if (recomputeStatus === 'done' && siteLatest) {
      setTab('summary');
    }
  }, [recomputeStatus, siteLatest]);

  return (
    <div className="state-panel-overlay" onClick={onClose}>
      <div className="state-panel-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="state-panel-header">
          <div className="state-panel-header__left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 0 1 10 10" />
              <path d="M12 2a10 10 0 0 0-10 10" />
              <line x1="12" y1="12" x2="16" y2="8" />
              <circle cx="12" cy="12" r="2" />
            </svg>
            Site State Snapshot
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {!isReadOnly && (
              <button
                className="state-btn state-btn--primary state-btn--sm"
                disabled={isRunning}
                onClick={() => triggerRecompute('all')}
                data-testid="state-panel-recompute"
              >
                {isRunning ? 'Running…' : '↺ Recompute'}
              </button>
            )}
            <button className="state-panel-close" onClick={onClose} title="Close">×</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="state-panel-tabs">
          <button
            className={`state-panel-tab${tab === 'summary' ? ' state-panel-tab--active' : ''}`}
            onClick={() => setTab('summary')}
          >
            Summary
          </button>
          <button
            className={`state-panel-tab${tab === 'history' ? ' state-panel-tab--active' : ''}`}
            onClick={() => setTab('history')}
          >
            History
          </button>
          <button
            className={`state-panel-tab${tab === 'assets' ? ' state-panel-tab--active' : ''}`}
            onClick={() => setTab('assets')}
          >
            Assets
          </button>
        </div>

        {/* Body */}
        <div className="state-panel-body">
          {recomputeStatus === 'done' && lastRecompute && (
            <RecomputeBanner result={lastRecompute} />
          )}
          {recomputeStatus === 'error' && (
            <div
              className="state-panel-recompute-summary"
              style={{
                background: 'var(--sl-color-danger-50)',
                border: '1px solid var(--sl-color-danger-200)',
                color: 'var(--sl-color-danger-800)',
              }}
            >
              Recompute failed. Check console for details.
            </div>
          )}

          {tab === 'summary' && (
            siteLatestLoading
              ? <div className="state-panel-loading">Loading…</div>
              : siteLatest
                ? <SummaryTab snapshot={siteLatest} />
                : <div className="state-panel-empty">No snapshot yet. Click Recompute to generate one.</div>
          )}

          {tab === 'history' && <HistoryTab />}

          {tab === 'assets' && currentSiteId && (
            <AssetsTab siteId={currentSiteId} />
          )}
          {tab === 'assets' && !currentSiteId && (
            <div className="state-panel-empty">No site loaded.</div>
          )}
        </div>

      </div>
    </div>
  );
}
